export interface VariableFilletEndpointInput {
    itemRef: string;
}

export interface VariableFilletEnsureInput {
    current?: VariableRadiusControlPoint[];
    itemRefs: string[];
    instanceCount?: number | null;
    radius?: number | null;
    expression?: string | null;
}

export interface VariableFilletInstanceInput {
    current?: VariableRadiusControlPoint[];
    itemRef: string;
    instanceIndex: number;
    instanceCount?: number | null;
}

export interface VariableFilletUpdateRadiusInput {
    current?: VariableRadiusControlPoint[];
    key: string;
    radius: number;
    expression?: string | null;
    slot?: 'radius1' | 'radius2';
}

export interface VariableFilletRemoveInput {
    /** 当前控制点集合。 */
    current?: VariableRadiusControlPoint[];
    /** 请求删除的控制点 key。 */
    key: string;
    /** 当前变量圆角边引用集合。 */
    itemRefs: string[];
    /** 当前实例点数量。 */
    instanceCount?: number | null;
    /** 补齐端点时使用的默认半径。 */
    radius?: number | null;
    /** 补齐端点时使用的默认半径表达式。 */
    expression?: string | null;
}

export interface VariableFilletRemoveAllInstancesInput {
    /** 当前控制点集合。 */
    current?: VariableRadiusControlPoint[];
    /** 当前变量圆角边引用集合。 */
    itemRefs: string[];
    /** 当前实例点数量。 */
    instanceCount?: number | null;
    /** 补齐端点时使用的默认半径。 */
    radius?: number | null;
    /** 补齐端点时使用的默认半径表达式。 */
    expression?: string | null;
}

export interface VariableFilletInstanceResult {
    points: VariableRadiusControlPoint[];
    selectedKey: string | null;
}

interface VariableFilletEndpointDescriptor {
    itemRef: string;
    label: string;
    parameter: number;
    vertexRef: string;
}

interface VariableFilletInterpolatedRadius {
    radius: number | null;
    expression: string | null;
    radius2: number | null;
    expression2: string | null;
}

/**
 * 变量大小圆角控制点解析器。
 *
 * 设计说明：
 * 1. SolidWorks 的变量圆角端点编号是整个选择集共享的 V 序列，不是每条边各自 V1/V2；
 * 2. 实例点 P 序列按用户选择顺序分配，不直接等同于边上的均分候选序号；
 * 3. 因此该类统一承载 V/P 编号与求解控制点之间的映射，避免 UI 面板、预览引导和 Worker 求解各自维护一套规则。
 */
export class VariableFilletControlPointResolver {
    /** 默认实例点数量。 */
    public static readonly DEFAULT_INSTANCE_COUNT = 3;

    /** 最小实例点数量。 */
    public static readonly MIN_INSTANCE_COUNT = 0;

    /** 最大实例点数量。 */
    public static readonly MAX_INSTANCE_COUNT = 24;

    /**
     * 归一化实例点数量。
     *
     * @param count 原始实例数。
     * @returns 合法实例数。
     */
    public clampInstanceCount(count: number | null | undefined): number {
        if (typeof count !== 'number' || !Number.isFinite(count)) {
            return VariableFilletControlPointResolver.DEFAULT_INSTANCE_COUNT;
        }
        return Math.max(
            VariableFilletControlPointResolver.MIN_INSTANCE_COUNT,
            Math.min(VariableFilletControlPointResolver.MAX_INSTANCE_COUNT, Math.round(count)),
        );
    }

    /**
     * 构建变量圆角控制点键。
     *
     * @param itemRef 目标边引用。
     * @param label 控制点标签。
     * @returns 控制点键。
     */
    public buildPointKey(itemRef: string, label: string): string {
        return `${itemRef}:${label}`;
    }

    /**
     * 构建实例候选点键。
     *
     * @param itemRef 目标边引用。
     * @param instanceIndex 候选实例序号。
     * @returns 候选点键。
     */
    public buildInstanceCandidateKey(itemRef: string, instanceIndex: number): string {
        return `${itemRef}:I${instanceIndex}`;
    }

    /**
     * 解析实例候选点键。
     *
     * @param key 候选点键。
     * @returns 解析结果；不是候选点时返回 null。
     */
    public parseInstanceCandidateKey(key: string): { itemRef: string; instanceIndex: number } | null {
        const separatorIndex = key.lastIndexOf(':I');
        if (separatorIndex < 0) {
            return null;
        }
        const itemRef = key.slice(0, separatorIndex);
        const instanceIndex = Number(key.slice(separatorIndex + 2));
        if (!itemRef || !Number.isInteger(instanceIndex) || instanceIndex <= 0) {
            return null;
        }
        return { itemRef, instanceIndex };
    }

    /**
     * 从控制点键解析尾部标签。
     *
     * @param key 控制点键。
     * @returns 标签。
     */
    public parsePointLabelFromKey(key: string): string {
        return key.slice(key.lastIndexOf(':') + 1);
    }

    /**
     * 解析实例点标签序号。
     *
     * @param label 控制点标签。
     * @returns P 点序号；不是 P 点时返回 null。
     */
    public parseInstanceLabelIndex(label: string): number | null {
        const matched = /^P(\d+)$/.exec(label);
        if (!matched) {
            return null;
        }
        const value = Number(matched[1]);
        return Number.isInteger(value) && value > 0 ? value : null;
    }

    /**
     * 补齐选中边集合的端点控制点，并保留已加入的实例点。
     *
     * @param input 补齐输入。
     * @returns 归一化后的控制点集合。
     */
    public ensureEndpointPoints(input: VariableFilletEnsureInput): VariableRadiusControlPoint[] {
        const itemRefs = this.dedupeItemRefs(input.itemRefs);
        const instanceCount = this.clampInstanceCount(input.instanceCount);
        const endpointDescriptors = this.resolveEndpointDescriptors(itemRefs);
        const endpoints = endpointDescriptors.map((descriptor) => (
            this.createEndpointPoint({
                descriptor,
                current: input.current ?? [],
                radius: input.radius ?? null,
                expression: input.expression ?? null,
            })
        ));
        const instances = this.normalizeInstancePoints({
            current: input.current ?? [],
            itemRefs,
            instanceCount,
        });
        return this.sortControlPoints(this.dedupePointsByKey([...endpoints, ...instances]));
    }

    /**
     * 写入或选择一个实例点。
     *
     * @param input 实例点输入。
     * @returns 更新后的控制点集合和实际选中 key。
     */
    public upsertInstancePoint(input: VariableFilletInstanceInput): VariableFilletInstanceResult {
        const instanceCount = this.clampInstanceCount(input.instanceCount);
        if (input.instanceIndex < 1 || input.instanceIndex > instanceCount) {
            return {
                points: this.ensureEndpointPoints({
                    current: input.current,
                    itemRefs: [
                        ...(input.current ?? []).map((point) => point.itemRef),
                        input.itemRef,
                    ],
                    instanceCount,
                }),
                selectedKey: null,
            };
        }

        const existing = this.findInstancePoint(input.current ?? [], input.itemRef, input.instanceIndex, instanceCount);
        if (existing) {
            return {
                points: this.ensureEndpointPoints({
                    current: input.current,
                    itemRefs: [
                        ...(input.current ?? []).map((point) => point.itemRef),
                        input.itemRef,
                    ],
                    instanceCount,
                }),
                selectedKey: existing.key,
            };
        }

        const nextLabel = this.allocateNextInstanceLabel(input.current ?? []);
        const point = this.createInstancePoint({
            current: input.current ?? [],
            itemRef: input.itemRef,
            instanceIndex: input.instanceIndex,
            instanceCount,
            label: nextLabel,
        });
        const points = this.ensureEndpointPoints({
            current: [
                ...(input.current ?? []),
                point,
            ],
            itemRefs: [
                ...(input.current ?? []).map((candidate) => candidate.itemRef),
                input.itemRef,
            ],
            instanceCount,
        });
        return {
            points,
            selectedKey: point.key,
        };
    }

    /**
     * 更新控制点半径。
     *
     * @param input 更新输入。
     * @returns 更新后的控制点集合。
     */
    public updateRadius(input: VariableFilletUpdateRadiusInput): VariableRadiusControlPoint[] {
        const points = input.current ?? [];
        const target = points.find((point) => point.key === input.key);
        const targetLabel = target?.label ?? this.parsePointLabelFromKey(input.key);
        return points.map((point) => {
            const sameTarget = point.key === input.key || (
                point.role === 'vertex'
                && target?.role === 'vertex'
                && point.label === targetLabel
            );
            if (!sameTarget) {
                return point;
            }
            if (input.slot === 'radius2') {
                return {
                    ...point,
                    radius2: input.radius,
                    expression2: input.expression ?? null,
                };
            }
            return {
                ...point,
                radius: input.radius,
                expression: input.expression ?? null,
            };
        });
    }

    /**
     * 删除变量圆角实例点。
     *
     * 说明：SolidWorks 的 V 点代表选中边的真实端点，属于变量圆角必备控制点；
     * P 点才是用户后续加入的实例点。因此删除请求必须在领域层判定角色，
     * 避免 UI 或调用方误删端点导致求解缺失 V1/V2。
     *
     * @param input 删除输入。
     * @returns 删除后的控制点集合。
     */
    public removeInstancePoint(input: VariableFilletRemoveInput): VariableRadiusControlPoint[] {
        const current = input.current ?? [];
        const target = current.find((point) => point.key === input.key);
        const nextCurrent = target?.role === 'instance'
            ? current.filter((point) => point.key !== input.key)
            : current;
        return this.ensureEndpointPoints({
            current: nextCurrent,
            itemRefs: input.itemRefs,
            instanceCount: input.instanceCount,
            radius: input.radius,
            expression: input.expression,
        });
    }

    /**
     * 删除所有变量圆角实例点。
     *
     * @param input 删除输入。
     * @returns 仅保留 V 点并补齐端点后的控制点集合。
     */
    public removeAllInstancePoints(
        input: VariableFilletRemoveAllInstancesInput,
    ): VariableRadiusControlPoint[] {
        return this.ensureEndpointPoints({
            current: (input.current ?? []).filter((point) => point.role !== 'instance'),
            itemRefs: input.itemRefs,
            instanceCount: input.instanceCount,
            radius: input.radius,
            expression: input.expression,
        });
    }

    /**
     * 将指定半径写入所有控制点。
     *
     * @param input 更新输入。
     * @returns 更新后的控制点集合。
     */
    public assignRadiusToAll(input: {
        current?: VariableRadiusControlPoint[];
        radius: number;
        expression?: string | null;
        radius2?: number;
        expression2?: string | null;
    }): VariableRadiusControlPoint[] {
        return (input.current ?? []).map((point) => ({
            ...point,
            radius: input.radius,
            expression: input.expression ?? null,
            ...(typeof input.radius2 === 'number' && Number.isFinite(input.radius2) && input.radius2 > 0
                ? {
                    radius2: input.radius2,
                    expression2: input.expression2 ?? null,
                }
                : {}),
        }));
    }

    /**
     * 解析控制点标签。
     *
     * @param point 控制点。
     * @returns 标签。
     */
    public resolvePointLabel(point: VariableRadiusControlPoint): string {
        return point.label ?? this.parsePointLabelFromKey(point.key);
    }

    /**
     * 按 SolidWorks 面板语义排序控制点。
     *
     * @param points 控制点集合。
     * @returns 排序后的控制点集合。
     */
    public sortControlPoints(points: VariableRadiusControlPoint[]): VariableRadiusControlPoint[] {
        return [...points].sort((left, right) => {
            const leftRank = this.resolveDisplayRank(left);
            const rightRank = this.resolveDisplayRank(right);
            if (leftRank.group !== rightRank.group) {
                return leftRank.group - rightRank.group;
            }
            if (leftRank.index !== rightRank.index) {
                return leftRank.index - rightRank.index;
            }
            const parameterDelta = left.parameter - right.parameter;
            if (Math.abs(parameterDelta) > 1e-9) {
                return parameterDelta;
            }
            return left.itemRef.localeCompare(right.itemRef);
        });
    }

    /**
     * 解析边端点描述。
     *
     * @param itemRefs 边引用集合。
     * @returns 端点描述集合。
     */
    private resolveEndpointDescriptors(itemRefs: string[]): VariableFilletEndpointDescriptor[] {
        const labelByVertexRef = new Map<string, string>();
        let nextVertexIndex = 1;
        return itemRefs.flatMap((itemRef) => {
            const endpointRefs = this.parseEdgeEndpointRefs(itemRef);
            return endpointRefs.map((vertexRef, endpointIndex) => {
                let label = labelByVertexRef.get(vertexRef);
                if (!label) {
                    label = `V${nextVertexIndex}`;
                    nextVertexIndex += 1;
                    labelByVertexRef.set(vertexRef, label);
                }
                return {
                    itemRef,
                    label,
                    vertexRef,
                    parameter: endpointIndex === 0 ? 0 : 1,
                };
            });
        });
    }

    /**
     * 创建端点控制点。
     *
     * @param input 创建输入。
     * @returns 端点控制点。
     */
    private createEndpointPoint(input: {
        descriptor: VariableFilletEndpointDescriptor;
        current: VariableRadiusControlPoint[];
        radius: number | null;
        expression: string | null;
    }): VariableRadiusControlPoint {
        const matched = input.current.find((point) => (
            point.role === 'vertex'
            && point.itemRef === input.descriptor.itemRef
            && (
                point.vertexRef === input.descriptor.vertexRef
                || point.label === input.descriptor.label
            )
        )) ?? input.current.find((point) => (
            point.role === 'vertex'
            && (
                point.vertexRef === input.descriptor.vertexRef
                || point.label === input.descriptor.label
            )
        ));
        return {
            key: this.buildPointKey(input.descriptor.itemRef, input.descriptor.label),
            itemRef: input.descriptor.itemRef,
            label: input.descriptor.label,
            role: 'vertex',
            vertexRef: input.descriptor.vertexRef,
            parameter: input.descriptor.parameter,
            radius: matched?.radius ?? input.radius,
            expression: matched?.expression ?? input.expression,
            radius2: matched?.radius2 ?? null,
            expression2: matched?.expression2 ?? null,
        };
    }

    /**
     * 创建实例控制点。
     *
     * @param input 创建输入。
     * @returns 实例控制点。
     */
    private createInstancePoint(input: {
        current: VariableRadiusControlPoint[];
        itemRef: string;
        instanceIndex: number;
        instanceCount: number;
        label: string;
    }): VariableRadiusControlPoint {
        const parameter = input.instanceIndex / (input.instanceCount + 1);
        const interpolatedRadius = this.interpolateRadiusForInstancePoint({
            current: input.current,
            itemRef: input.itemRef,
            parameter,
        });
        return {
            key: this.buildPointKey(input.itemRef, input.label),
            itemRef: input.itemRef,
            label: input.label,
            role: 'instance',
            instanceIndex: input.instanceIndex,
            parameter,
            radius: interpolatedRadius.radius,
            expression: interpolatedRadius.expression,
            radius2: interpolatedRadius.radius2,
            expression2: interpolatedRadius.expression2,
        };
    }

    /**
     * 为新实例点计算默认半径。
     *
     * SolidWorks 在用户点击实例点时会立即把该点加入半径列表，且默认值取当前
     * 边上已有 V/P 控制点在该参数位置的插值，而不是空值。这样端点同为
     * R1=10/R2=30 时，新 P 点默认也会显示并求解为 R1=10/R2=30。
     *
     * @param input 插值输入。
     * @returns 插值得到的默认半径。
     */
    private interpolateRadiusForInstancePoint(input: {
        current: VariableRadiusControlPoint[];
        itemRef: string;
        parameter: number;
    }): VariableFilletInterpolatedRadius {
        const controlPoints = input.current
            .filter((point) => point.itemRef === input.itemRef)
            .filter((point) => (
                Number.isFinite(point.parameter)
                && typeof point.radius === 'number'
                && Number.isFinite(point.radius)
                && point.radius > 0
            ))
            .sort((left, right) => left.parameter - right.parameter);
        if (controlPoints.length === 0) {
            return {
                radius: null,
                expression: null,
                radius2: null,
                expression2: null,
            };
        }
        const leftPoint = [...controlPoints]
            .reverse()
            .find((point) => point.parameter <= input.parameter) ?? controlPoints[0]!;
        const rightPoint = controlPoints
            .find((point) => point.parameter >= input.parameter) ?? controlPoints[controlPoints.length - 1]!;
        const radius = this.interpolateRadiusSlot(leftPoint, rightPoint, input.parameter, 'radius');
        const radius2 = this.interpolateRadiusSlot(leftPoint, rightPoint, input.parameter, 'radius2');
        return {
            radius,
            expression: null,
            radius2,
            expression2: null,
        };
    }

    /**
     * 按控制点参数线性插值单个半径槽位。
     *
     * @param leftPoint 左侧控制点。
     * @param rightPoint 右侧控制点。
     * @param parameter 目标参数。
     * @param slot 半径槽位。
     * @returns 插值结果。
     */
    private interpolateRadiusSlot(
        leftPoint: VariableRadiusControlPoint,
        rightPoint: VariableRadiusControlPoint,
        parameter: number,
        slot: 'radius' | 'radius2',
    ): number | null {
        const leftRadius = leftPoint[slot];
        const rightRadius = rightPoint[slot];
        if (
            typeof leftRadius !== 'number'
            || !Number.isFinite(leftRadius)
            || leftRadius <= 0
            || typeof rightRadius !== 'number'
            || !Number.isFinite(rightRadius)
            || rightRadius <= 0
        ) {
            return null;
        }
        const range = rightPoint.parameter - leftPoint.parameter;
        if (Math.abs(range) <= 1e-9) {
            return leftRadius;
        }
        const ratio = Math.min(1, Math.max(0, (parameter - leftPoint.parameter) / range));
        return leftRadius + ((rightRadius - leftRadius) * ratio);
    }

    /**
     * 归一化已有实例点。
     *
     * @param input 归一化输入。
     * @returns 合法实例点集合。
     */
    private normalizeInstancePoints(input: {
        current: VariableRadiusControlPoint[];
        itemRefs: string[];
        instanceCount: number;
    }): VariableRadiusControlPoint[] {
        const itemRefSet = new Set(input.itemRefs);
        return input.current.reduce<VariableRadiusControlPoint[]>((points, point) => {
            if (point.role !== 'instance' || !itemRefSet.has(point.itemRef)) {
                return points;
            }
            const instanceIndex = point.instanceIndex ?? Math.round(point.parameter * (input.instanceCount + 1));
            const label = this.resolvePointLabel(point);
            if (
                !this.parseInstanceLabelIndex(label)
                || !Number.isInteger(instanceIndex)
                || instanceIndex < 1
                || instanceIndex > input.instanceCount
            ) {
                return points;
            }
            points.push({
                ...point,
                key: this.buildPointKey(point.itemRef, label),
                label,
                role: 'instance' as const,
                instanceIndex,
                parameter: instanceIndex / (input.instanceCount + 1),
            });
            return points;
        }, []);
    }

    /**
     * 查找已加入的实例点。
     *
     * @param points 当前控制点。
     * @param itemRef 边引用。
     * @param instanceIndex 候选实例序号。
     * @param instanceCount 实例总数。
     * @returns 已存在的实例点。
     */
    private findInstancePoint(
        points: VariableRadiusControlPoint[],
        itemRef: string,
        instanceIndex: number,
        instanceCount: number,
    ): VariableRadiusControlPoint | null {
        const parameter = instanceIndex / (instanceCount + 1);
        return points.find((point) => (
            point.role === 'instance'
            && point.itemRef === itemRef
            && (
                point.instanceIndex === instanceIndex
                || Math.abs(point.parameter - parameter) <= 1e-9
            )
        )) ?? null;
    }

    /**
     * 分配下一个 P 点标签。
     *
     * @param points 当前控制点。
     * @returns 新 P 点标签。
     */
    private allocateNextInstanceLabel(points: VariableRadiusControlPoint[]): string {
        const maxIndex = points.reduce((currentMax, point) => {
            if (point.role !== 'instance') {
                return currentMax;
            }
            const index = this.parseInstanceLabelIndex(this.resolvePointLabel(point));
            return index ? Math.max(currentMax, index) : currentMax;
        }, 0);
        return `P${maxIndex + 1}`;
    }

    /**
     * 解析边引用中的两个端点引用。
     *
     * @param itemRef 边引用。
     * @returns 两个端点引用。
     */
    private parseEdgeEndpointRefs(itemRef: string): [string, string] {
        const parts = itemRef.split('|');
        if (parts.length === 2 && parts[0] && parts[1]) {
            return [parts[0], parts[1]];
        }
        return [`${itemRef}:start`, `${itemRef}:end`];
    }

    /**
     * 去重边引用。
     *
     * @param itemRefs 原始边引用。
     * @returns 去重后的边引用。
     */
    private dedupeItemRefs(itemRefs: string[]): string[] {
        return Array.from(new Set(itemRefs.filter((itemRef) => itemRef.length > 0)));
    }

    /**
     * 按 key 去重控制点。
     *
     * @param points 控制点集合。
     * @returns 去重后的控制点。
     */
    private dedupePointsByKey(points: VariableRadiusControlPoint[]): VariableRadiusControlPoint[] {
        return Array.from(points.reduce<Map<string, VariableRadiusControlPoint>>((map, point) => {
            if (!map.has(point.key)) {
                map.set(point.key, point);
            }
            return map;
        }, new Map()).values());
    }

    /**
     * 解析展示排序权重。
     *
     * @param point 控制点。
     * @returns 排序权重。
     */
    private resolveDisplayRank(point: VariableRadiusControlPoint): { group: number; index: number } {
        const label = this.resolvePointLabel(point);
        const vertexMatched = /^V(\d+)$/.exec(label);
        if (vertexMatched) {
            return { group: 0, index: Number(vertexMatched[1]) };
        }
        const instanceIndex = this.parseInstanceLabelIndex(label);
        if (instanceIndex) {
            return { group: 1, index: instanceIndex };
        }
        return { group: 2, index: Number.MAX_SAFE_INTEGER };
    }
}
